export { SHIELD_PATH, SHIELD_INSET_PATH } from "./shieldPath";
export const VIEW = { w: 400, h: 644 };
export const VIEWBOX = "-3 -3 406 650";
export const SHIELD_RATIO = 400 / 643.7;
export const PHOTO_BOX = { x: 104, y: 22, w: 290, h: 400 };
export const PHOTO_BOX_ROW = { x: 60, y: 22, w: 280, h: 420 };
export const NUMBER_PLACEMENTS = [["behind", "Behind photo"], ["name", "Beside name"], ["corner", "Under rating"]];
export const LAYOUTS = [["columns", "Two columns"], ["row", "Single row"]];
export const photoBoxFor = (layout) => (layout === "row" ? PHOTO_BOX_ROW : PHOTO_BOX);
export const FULL_BOX = { x: 0, y: 0, w: 400, h: 644 };

const GAA_STATS = (skill) => [
  { key: "PAC", label: "Pace" },
  { key: "SKL", label: skill },
  { key: "SHO", label: "Shooting" },
  { key: "PAS", label: "Passing" },
  { key: "STR", label: "Strength" },
  { key: "WRK", label: "Work rate" },
];
const GAA_POSITIONS = ["GK", "RCB", "FB", "LCB", "RHB", "CHB", "LHB", "MF", "RHF", "CHF", "LHF", "RCF", "FF", "LCF"];
const GAA_NAMES = {
  RCB: "Right corner-back (2)", LCB: "Left corner-back (4)", RHB: "Right half-back / right wing-back (5)", RWB: "Right wing-back (5)",
  LHB: "Left half-back / left wing-back (7)", LWB: "Left wing-back (7)", MID: "Midfielder (8 and 9)", RHF: "Right half-forward / right wing-forward (10)",
  RWF: "Right wing-forward (10)", LHF: "Left half-forward / left wing-forward (12)", LWF: "Left wing-forward (12)", RCF: "Right corner-forward (13)", LCF: "Left corner-forward (15)",
};
const GAA_NUMBERED = { GK: "Goalkeeper (1)", FB: "Full-back (3)", CHB: "Centre half-back (6)", MF: "Midfielder (8 and 9)", CHF: "Centre half-forward (11)", FF: "Full-forward (14)" };
export const positionName = (p, sport) => (["gaa", "hurling"].includes(sport) && (GAA_NUMBERED[p] || GAA_NAMES[p])) || POSITION_NAMES[p] || p;
export const POSITION_NAMES = {
  GK: "Goalkeeper", RB: "Right back", CB: "Centre back / Corner back", LB: "Left back", CDM: "Defensive midfield", CM: "Centre midfield",
  CAM: "Attacking midfield", LW: "Left wing", RW: "Right wing", ST: "Striker", CF: "Centre forward / Corner forward",
  FB: "Full back", HB: "Half back", CHB: "Centre half back", MF: "Midfield", HF: "Half forward", CHF: "Centre half forward", FF: "Full forward",
  ...GAA_NAMES, PROP: "Prop", HK: "Hooker", LOCK: "Lock", FL: "Flanker", N8: "Number 8", SH: "Scrum half", FH: "Fly half", CTR: "Centre", WG: "Wing",
};

export const TEMPLATES = {
  football: {
    name: "Football",
    stats: [
      { key: "PAC", label: "Pace" },
      { key: "SHO", label: "Shooting" },
      { key: "PAS", label: "Passing" },
      { key: "DRI", label: "Dribbling" },
      { key: "DEF", label: "Defending" },
      { key: "PHY", label: "Physicality" },
    ],
    positions: ["GK", "RB", "CB", "LB", "CDM", "CM", "CAM", "LW", "RW", "ST", "CF"],
  },
  gaa: { name: "Gaelic football", stats: GAA_STATS("Skill"), positions: GAA_POSITIONS },
  hurling: { name: "Hurling / Camogie", stats: GAA_STATS("Stick skill"), positions: GAA_POSITIONS },
  rugby: {
    name: "Rugby",
    stats: [
      { key: "PAC", label: "Pace" },
      { key: "TKL", label: "Tackling" },
      { key: "HAN", label: "Handling" },
      { key: "KCK", label: "Kicking" },
      { key: "PWR", label: "Power" },
      { key: "FIT", label: "Fitness" },
    ],
    positions: ["PROP", "HK", "LOCK", "FL", "N8", "SH", "FH", "CTR", "WG", "FB"],
  },
};
export const tplOf = (sport) => TEMPLATES[sport] || TEMPLATES.football;
export const statsFor = (sport) => tplOf(sport).stats;
export const STATS = TEMPLATES.football.stats;

// Values carry over by slot so switching sport never loses entered numbers.
export const switchSport = (d, sport) => {
  const old = statsFor(d.sport).map((s) => d.stats[s.key] ?? "");
  const stats = Object.fromEntries(statsFor(sport).map((s, i) => [s.key, old[i] ?? ""]));
  const keepPos = tplOf(sport).positions.includes(d.player.position) || ["", "custom"].includes(d.player.position);
  return { ...d, sport, stats, player: keepPos ? d.player : { ...d.player, position: "" } };
};

export const SIZES = [
  { id: "small", name: "Small", w: 210, h: 300, price: 1600, club: 500 },
  { id: "medium", name: "Medium", w: 300, h: 420, price: 2900, recommended: true, club: 900 },
  { id: "large", name: "Large", w: 420, h: 594, price: 3900, club: 1200 },
  { id: "xl", name: "Extra Large", w: 594, h: 840, price: 6500, club: 1500 },
];
export const sizeOf = (id) => SIZES.find((s) => s.id === id) || SIZES[1];
export const shieldMm = (s) => ({ w: Math.round(s.h * SHIELD_RATIO), h: s.h });
export const eur = (c) => new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR" }).format((c || 0) / 100);

export const PALETTES = [
  { id: "red-white", name: "Red & white", primary: "#C8102E", secondary: "#FFFFFF", accent: "#F5C542", border: "#F4F4F5", text: "#FFFFFF" },
  { id: "blue-white", name: "Blue & white", primary: "#1D4ED8", secondary: "#FFFFFF", accent: "#7DD3FC", border: "#E2E8F0", text: "#FFFFFF" },
  { id: "green-white", name: "Green & white", primary: "#047857", secondary: "#FFFFFF", accent: "#FBBF24", border: "#F1F5F9", text: "#FFFFFF" },
  { id: "black-amber", name: "Black & amber", primary: "#111111", secondary: "#F59E0B", accent: "#FCD34D", border: "#F59E0B", text: "#FFFFFF" },
  { id: "maroon-white", name: "Maroon & white", primary: "#7A1F2B", secondary: "#FFFFFF", accent: "#D4AF37", border: "#F1E4C3", text: "#FFFFFF" },
  { id: "navy-gold", name: "Navy & gold", primary: "#0F1E3D", secondary: "#D4AF37", accent: "#F5D76E", border: "#D4AF37", text: "#FFFFFF" },
];

export const BACKGROUNDS = [
  { id: "solid", name: "Solid colour" },
  { id: "gradient", name: "Gradient" },
  { id: "stripes", name: "Club stripes", variants: true },
  { id: "split", name: "Split colours", variants: true },
  { id: "geometric", name: "Geometric" },
  { id: "abstract", name: "Abstract" },
  { id: "upload", name: "Upload your own" },
];

export const COUNTRIES = [
  ["ie", "Ireland"], ["gb-nir", "Northern Ireland"], ["gb-eng", "England"], ["gb-sct", "Scotland"], ["gb-wls", "Wales"],
  ["gb", "United Kingdom"], ["fr", "France"], ["de", "Germany"], ["es", "Spain"], ["pt", "Portugal"], ["it", "Italy"],
  ["nl", "Netherlands"], ["be", "Belgium"], ["dk", "Denmark"], ["se", "Sweden"], ["no", "Norway"], ["fi", "Finland"],
  ["is", "Iceland"], ["pl", "Poland"], ["cz", "Czechia"], ["sk", "Slovakia"], ["at", "Austria"], ["ch", "Switzerland"],
  ["hr", "Croatia"], ["rs", "Serbia"], ["si", "Slovenia"], ["hu", "Hungary"], ["ro", "Romania"], ["bg", "Bulgaria"],
  ["gr", "Greece"], ["tr", "Türkiye"], ["ua", "Ukraine"], ["lt", "Lithuania"], ["lv", "Latvia"], ["ee", "Estonia"],
  ["al", "Albania"], ["ba", "Bosnia & Herzegovina"], ["us", "United States"], ["ca", "Canada"], ["mx", "Mexico"],
  ["br", "Brazil"], ["ar", "Argentina"], ["uy", "Uruguay"], ["co", "Colombia"], ["cl", "Chile"], ["jp", "Japan"],
  ["kr", "South Korea"], ["cn", "China"], ["in", "India"], ["pk", "Pakistan"], ["au", "Australia"], ["nz", "New Zealand"],
  ["za", "South Africa"], ["ng", "Nigeria"], ["gh", "Ghana"], ["sn", "Senegal"], ["ci", "Côte d'Ivoire"], ["cm", "Cameroon"],
  ["ma", "Morocco"], ["eg", "Egypt"], ["dz", "Algeria"], ["tn", "Tunisia"], ["sa", "Saudi Arabia"], ["ph", "Philippines"],
];

export const flagUrl = (code) => `https://flagcdn.com/${code}.svg`;

const emptyPlacement = () => ({ file: null, crop: null, zoom: 1, x: 0, y: 0 });
const emptyStats = (sport) => Object.fromEntries(statsFor(sport).map((s) => [s.key, ""]));

export const defaultDesign = () => {
  const { id, name, ...colours } = PALETTES[5];
  return {
    background: { style: "abstract", variant: "diagonal", upload: emptyPlacement(), overlay: { enabled: false, color: "#0F172A", opacity: 0.35 } },
    colours,
    photo: { ...emptyPlacement(), fade: true },
    player: { name: "", rating: "", position: "ST", customPosition: "", clubName: "", shirtNumber: "", numberPlacement: "behind", numberColor: "" },
    crest: { file: null, show: true },
    flag: { code: "ie", show: true, kind: "country", county: "ker", province: "mun" },
    stats: emptyStats(),
    guide: true,
    textPanel: true,
    sport: "football",
    size: "medium",
    layout: "columns",
  };
};

// Fills gaps in older/partial saved designs so every layer can render.
export const withDefaults = (d) => {
  const def = defaultDesign();
  const merged = { ...def, ...d };
  for (const k of ["background", "colours", "photo", "player", "crest", "flag", "stats"]) merged[k] = { ...def[k], ...(d?.[k] || {}) };
  merged.background.upload = { ...def.background.upload, ...(d?.background?.upload || {}) };
  merged.background.overlay = { ...def.background.overlay, ...(d?.background?.overlay || {}) };
  return merged;
};

export const newPlayerFrom = (d) => ({
  ...d,
  photo: { ...emptyPlacement(), fade: d.photo.fade },
  player: { name: "", rating: "", position: "", customPosition: "", clubName: d.player.clubName },
  stats: emptyStats(d.sport),
});

export const namedPlayer = (base, name) => { const d = newPlayerFrom(base); return { ...d, player: { ...d.player, name } }; };

export const positionLabel = (p) => (p.position === "custom" ? p.customPosition : p.position);

// Whole number 0-100, or "" for empty.
export function parseWhole(raw, max = 100) {
  const s = String(raw).trim();
  if (s === "") return { ok: true, value: "" };
  if (s.startsWith("-")) return { ok: false, error: `Negative numbers aren't allowed. Enter 0 to ${max}.` };
  if (/[.,]/.test(s)) return { ok: false, error: "Whole numbers only, no decimals." };
  if (!/^\d+$/.test(s)) return { ok: false, error: `Numbers only. Enter a whole number from 0 to ${max}.` };
  const n = parseInt(s, 10);
  if (n > max) return { ok: false, error: `The maximum is ${max}.` };
  return { ok: true, value: String(n) };
}
